import * as React from 'react';
import type { ReactNode } from 'react';

export type TreeNodeVariant = 'horizontal' | 'vertical' | 'assistant';
export const TreeNodeVariantStrings: {
  horizontal: TreeNodeVariant;
  vertical: TreeNodeVariant;
  assistant: TreeNodeVariant;
} = {
  horizontal: 'horizontal',
  vertical: 'vertical',
  assistant: 'assistant',
};

export interface TreeNodeProps {
  /** The node label */
  label: React.ReactNode;
  className?: string;
  variant: TreeNodeVariant;
  isLast: boolean;
  children?: ReactNode;
}

const NODE_CLASS_MAP: Record<TreeNodeVariant, string> = {
  horizontal: 'orgTreeNode__node--horizontal',
  vertical: 'orgTreeNode__node--vertical',
  assistant: 'orgTreeNode__node--assistant',
};

const LINE_CLASS_MAP: Record<TreeNodeVariant, string> = {
  horizontal: 'orgTreeNode__lines--horizontal',
  vertical: 'orgTreeNode__lines--vertical',
  assistant: 'orgTreeNode__lines--assistant',
};

const getChildrenByVariant = (target: TreeNodeVariant, children: ReactNode) =>
  React.Children.toArray(children).filter(
    (child): child is React.ReactElement<typeof TreeNode> =>
      React.isValidElement(child) && child.props.variant === target
  );

function TreeNode({
  children,
  label,
  variant,
  isLast,
  className,
}: TreeNodeProps) {
  const assistantChildren = getChildrenByVariant(
    TreeNodeVariantStrings.assistant,
    children
  );

  const horizontalChildren = getChildrenByVariant(
    TreeNodeVariantStrings.horizontal,
    children
  );

  const verticalChildren = getChildrenByVariant(
    TreeNodeVariantStrings.vertical,
    children
  );

  const nodeClass = NODE_CLASS_MAP[variant];
  const lineClass = LINE_CLASS_MAP[variant];

  const isVertical = variant === TreeNodeVariantStrings.vertical;

  let specialTopLines = '';

  if (verticalChildren.length > 0) {
    specialTopLines = 'orgTreeNode__lines--horizontalWithChildren';
  }

  return (
    <li
      className={`orgTreeNode ${[
        nodeClass,
        lineClass,
        className,
        specialTopLines,
      ]
        .filter(Boolean)
        .join(' ')}`}
      style={{
        ...(verticalChildren.length ? { textAlign: 'left' } : {}),
      }}
    >
      {isVertical && (
        <>
          <div
            style={{
              position: 'absolute',
              width: 'var(--tree-line-width)',
              backgroundColor: 'white',
              zIndex: -1, // Lower zIndex to place the line behind other elements
              height: isLast ? '30px' : '100%', // Default height
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '40px',
              left: 0,
              right: '50%',
              height: 'var(--tree-line-width)',
              backgroundColor: 'white',
              zIndex: -1, // Lower zIndex to place the line behind other elements
            }}
          />
        </>
      )}
      <div
        style={{
          paddingLeft: isVertical ? 'var(--tree-node-padding)' : 0,
        }}
      >
        {label}
      </div>
      {assistantChildren.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: '50%',
              width: 'var(--tree-line-width)',
              backgroundColor: 'white',
            }}
          />
          {assistantChildren.map((child, index) => (
            <div
              key={index}
              style={{
                paddingTop: '10px',
                display: 'flex',
                justifyContent:
                  assistantChildren.length === 1 || index % 2 === 0
                    ? 'flex-end'
                    : 'flex-start',
                alignItems: 'center',
                position: 'relative',
                paddingInline: 'var(--tree-node-padding)',
                textAlign: index % 2 === 0 ? 'right' : 'left',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  width: 'var(--tree-node-padding)',
                  height: 'var(--tree-line-width)',
                  backgroundColor: 'white',
                  left: index % 2 === 0 ? 'auto' : 0,
                  right: index % 2 === 0 ? 0 : 'auto',
                }}
              />
              {child}
            </div>
          ))}
        </div>
      )}
      {verticalChildren.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            position: 'relative',
            zIndex: 1,
            paddingLeft: 'var(--tree-node-padding)',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              position: 'relative',
              textAlign: 'center',
            }}
          >
            <div>{verticalChildren}</div>
          </div>
        </div>
      )}
      {horizontalChildren.length > 0 && (
        <ul className={'orgTreeNode__children'}>{horizontalChildren}</ul>
      )}
    </li>
  );
}

export default TreeNode;
